import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import {
  getSpecificChannel,
  testChannel,
} from "../../../redux/features/Notifications";
import { setHideBreadCrumb } from "../../../redux/features/hideBreadCrumb/hideBreadCrumb";
import { disableSwitcher } from "../../../redux/features/multiEntitySwitcher/multiEntitySwitcher";

import ICONS from "../../../assets/Icons";
import StatusContainer from "../../../components/_utils/CommonStatus/StatusContainer";
import TabsWeapperSection from "../../../components/_utils/CommonStatus/StatusSubComponents/TabsWrapperSection";
import InfoCard from "../../../components/_utils/CommonStatus/StatusSubComponents/StatusCardContainer/InfoCard";
import accountNameStandardised from "../../../utils/accountNamestandardised";
import { GlobalConfiguration } from "../../../utils/configurationProvider";
import CommonToast from "../../../components/_utils/CommonToast";

const ChannelSuccess = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [isTestingNotification, setIsTestingNotification] = useState(false);
  const [isTestReset, setIsTestReset] = useState(false);

  const isEditFlow = location.pathname.includes("/edit_channel_success/");
  const editedFields = location.state?.editedFields || {};

  const { getSpecificChannelData } = useSelector(
    (state) => state.notificationsSlice,
  );

  const channelData = getSpecificChannelData;

  const providerLabelMap = {
    gchat: "Google Chat",
    slack: "Slack",
    teams: "Microsoft Teams",
    generic: "Custom Webhook",
  };

  const providerDisplayName = providerLabelMap[channelData?.provider];
  const isActive = !channelData?.is_active;

  const testNotificationHandler = async () => {
    if (channelData?.verification_status === "failed") {
      toast.dismiss();
      CommonToast({
        text: "Channel linkage failed.",
        icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
        variant: "var(--error)",
        btnProps: {
          isDisabled: false,
          enabledTextColor: "var(--text-primary)",
          enabledBgColor: "var(--primary-bg)",
        },
      });
      return;
    }

    if (isTestReset) {
      toast.dismiss();
      CommonToast({
        text: "Test message limit reached. Try again after 10 seconds.",
        icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
        variant: "var(--error)",
        btnProps: {
          isDisabled: false,
          enabledTextColor: "var(--text-primary)",
          enabledBgColor: "var(--primary-bg)",
        },
      });
      return;
    }

    try {
      setIsTestingNotification(true);
      const response = await dispatch(testChannel({ id }));

      if (response?.payload?.status === true) {
        toast.dismiss();
        CommonToast({
          text: "Test message sent successfully..",
          icon: ICONS?.SELECT_ALL_WITH_WHITE_FILLED_CIRCLE,
          variant: "var(--success)",
          btnProps: {
            isDisabled: false,
            enabledTextColor: "var(--text-primary)",
            enabledBgColor: "var(--primary-bg)",
          },
        });
      }

      setIsTestReset(true);

      setTimeout(() => {
        setIsTestReset(false);
      }, 10000);
    } finally {
      setIsTestingNotification(false);
    }
  };

  const dashboardHandler = () => {
    navigate("/home/notification_dashboard", { replace: true });
  };

  const viewDetailsHandler = () => {
    navigate(
      `/home/notification_dashboard/channel_view_all/channel_detail_page/${id}`,
      { replace: true },
    );
  };

  useEffect(() => {
    if (id) {
      dispatch(getSpecificChannel({ id }));
    }
    dispatch(disableSwitcher());
    dispatch(setHideBreadCrumb({ hide: true }));

    return () => {
      dispatch(setHideBreadCrumb({ hide: false }));
    };
  }, [id, dispatch]);

  const buttonRows = [
    [
      {
        btnText: "Test notification",
        enabledBgColor: "var(--text-primary)",
        enabledTextColor: "var(--positive-bg)",
        icon: ICONS.TEST_NOTIFICATION,
        handleFunction: testNotificationHandler,
        iconToBeAlign: "left",
        fullWidthRequired: true,
        imgColor: "var(--positive-bg)",
        isDisabled: isTestReset || isTestingNotification,
      },
      {
        btnText: "View details",
        enabledBgColor: "var(--text-primary)",
        enabledTextColor: "var(--positive-bg)",
        icon: ICONS.VIEW_DETAILS,
        handleFunction: viewDetailsHandler,
        iconToBeAlign: "left",
        fullWidthRequired: true,
        imgColor: "var(--positive-bg)",
      },
      {
        btnText: "Go to dashboard",
        enabledBgColor: "var(--bg-positive-bg)",
        enabledTextColor: "var(--text-primary)",
        icon: ICONS.GO_TO_DASHBOARD,
        handleFunction: dashboardHandler,
        iconToBeAlign: "left",
        fullWidthRequired: true,
        outLineColor: "var(--text-primary)",
        imgColor: "var(--text-primary)",
        hoverOption: "dark",
      },
    ],
  ];

  return (
    <div>
      <StatusContainer
        status="success"
        hoverOption="white"
        buttonRows={buttonRows}
        title={
          isEditFlow
            ? "Channel details updated successfully."
            : "Channel created successfully."
        }
      >
        <TabsWeapperSection title="Channel details">
          <>
            {isEditFlow ? (
              <>
                {editedFields?.channelTarget && (
                  <InfoCard
                    bgColor="var(--positive-card)"
                    rows={[
                      {
                        leftTitle: providerDisplayName,
                        leftSubTitle: "Target channel",
                      },
                    ]}
                  />
                )}

                {editedFields?.channelName && (
                  <InfoCard
                    bgColor="var(--positive-card)"
                    rows={[
                      {
                        leftTitle: accountNameStandardised(channelData?.name),
                        leftSubTitle: "Channel name",
                      },
                    ]}
                  />
                )}
                {editedFields?.channelUrl && (
                  <InfoCard
                    bgColor="var(--positive-card)"
                    rows={[
                      {
                        leftTitle: "Webhook URL configured",
                        leftSubTitle: "Channel URL",
                      },
                    ]}
                  />
                )}
              </>
            ) : (
              <>
                <InfoCard
                  bgColor="var(--positive-card)"
                  rows={[
                    {
                      leftTitle: providerDisplayName,
                      leftSubTitle: "Target channel",
                    },
                  ]}
                />

                <InfoCard
                  bgColor="var(--positive-card)"
                  rows={[
                    {
                      leftTitle: accountNameStandardised(channelData?.name),
                      leftSubTitle: "Channel name",
                    },
                  ]}
                />

                <InfoCard
                  bgColor="var(--positive-card)"
                  rows={[
                    {
                      leftTitle: "Webhook URL configured",
                      leftSubTitle: "Channel URL",
                    },
                  ]}
                />
              </>
            )}
          </>
        </TabsWeapperSection>
      </StatusContainer>
    </div>
  );
};

export default ChannelSuccess;
